import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { memo } from 'react';
import { Lang } from '../../_configs/types';
import { useSettingsContext } from '../../context/settings';
import useLocales from '../../hooks/useLocales';
import { SourceList } from '../../modules/_definition/types';
import { generateLink } from '../../utils/generateLink';
import formatText from '../../utils/texte/formatText';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import Iconify from '../iconify/component';
import { getLowestRate } from './helpers';
import type { CarPriceGridProps } from './types';

function CarPriceGrid(props: CarPriceGridProps) {
    const { accessoiresPrice, car, carActive, isProbefahrt, mini, module, showMonthlyRate, smaller, sourceList } = props;
    const settings = useSettingsContext();
    const { currentLang, translate } = useLocales();

    const carLink = `${generateLink({ car, module, pathOnly: false, settings, type: 'car' })}#calculatorSection`;
    const lowestRate = showMonthlyRate || getLowestRate(car);

    const price = (
        <Stack alignItems="center" justifyContent="center" textAlign="center" sx={{ [getStyleQuery()]: { flexGrow: 1, width: 1 } }}>
            <Typography component="p" variant={smaller ? 'h6' : 'h5'}>
                {car?.preis?.vk?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, minimumFractionDigits: 0 }) || '-'}
            </Typography>
            {car?.preis?.mwst === 1 ? (
                <>
                    <Typography component="p" variant="caption" color="text.secondary">
                        {`${car?.preis?.vkn?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, minimumFractionDigits: 0 }) || '-'} (${translate('common:common.netto')})`}
                    </Typography>
                    {mini && !checkUndefinedNullOrEmpty(accessoiresPrice) && !isProbefahrt && (
                        <Typography component="p" variant="caption" color="text.secondary">
                            {formatText(translate('common:carView.accessoires.withAccessoiresPrice'), accessoiresPrice)}
                        </Typography>
                    )}
                </>
            ) : (
                currentLang?.value === Lang.deDE && (
                    <Tooltip title={translate('common:carTile.priceGrid.diff25aDesc')}>
                        <Typography component="p" variant="caption" color="text.secondary">
                            {translate('common:carTile.priceGrid.diff25a')}
                        </Typography>
                    </Tooltip>
                )
            )}
        </Stack>
    );

    const rate = (
        <Stack alignItems="center" justifyContent="center" sx={{ [getStyleQuery()]: { flexGrow: 1, width: 1 } }}>
            <Typography component="p" variant={smaller ? 'h6' : 'h5'}>
                {`ab ${lowestRate?.rate?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}${carActive ? ' ¹' : ''}`}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
                <Typography component="p" variant="caption" color="text.secondary" whiteSpace="nowrap">
                    {translate(lowestRate?.type === 'leasingComm' ? 'common:carView.calculator.fields.type.leasing.prevText' : `common:carView.calculator.fields.type.${lowestRate?.type}.prevText`)}
                </Typography>
                {!mini && (
                    // Info Button Calculator Link
                    <IconButton
                        aria-label={`${translate(lowestRate?.type === 'leasingComm' ? 'common:carView.calculator.fields.type.leasing.prevText' : `common:carView.calculator.fields.type.${lowestRate?.type}.prevText`)} Info`}
                        role="link"
                        // href={carLink}
                        size="small"
                        sx={{
                            [getStyleQuery()]: {
                                m: -1,
                                '& > svg': {
                                    width: 16,
                                    height: 16,
                                    minWidth: 16,
                                },
                            }
                        }}
                        onClick={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            location.href = carLink;
                        }}
                    >
                        <Iconify width="1em" icon="eva:info-fill" />
                    </IconButton>
                )}
            </Stack>
        </Stack>
    );

    return (
        <Stack
            data-testid="CarPriceGrid_Container"
            gap={1}
            direction={!mini ? 'row' : 'column'}
            divider={<Divider orientation={!mini ? 'vertical' : 'horizontal'} flexItem sx={{ borderStyle: 'dashed' }} />}
            sx={{ pr: sourceList === SourceList.Contact ? 1 : undefined }}
        >
            {!checkUndefinedNullOrEmpty(showMonthlyRate) ? rate : price}
            {!mini && checkUndefinedNullOrEmpty(showMonthlyRate) && !checkUndefinedNullOrEmpty(lowestRate) && rate}
        </Stack>
    );
}

export default memo(CarPriceGrid);
